/**
 * Reports in a conversation (docs/REPORTS.md §4): a report's card stands out from the talk around it (an accent edge,
 * its label and period above it), and a series of them shows as one, its newest, the earlier ones a click away.
 */

import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { CommentOut, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { commentAnchor } from "../../lib/conversation.ts";
import { useItemPicks } from "../../lib/itemPicks.ts";
import { periodText, type Series } from "../../lib/reports.ts";
import { location } from "../../lib/router.ts";
import { InlineButton } from "../../ui/Button.tsx";
import { Badge } from "../../ui/misc.tsx";
import { RichText } from "./richText.tsx";

/** What it's based on: how many items, opening the list of them (those the reader sees). */
function BasedOn({ space, ids }: { space: SpaceOut; ids: readonly string[] }) {
  const [open, setOpen] = useState(false);
  const pick = useItemPicks(space, open ? ids : []);
  return (
    <>
      <InlineButton size="xs" aria-expanded={open} onClick={() => setOpen(!open)}>
        {t("reports.basedOn", { count: ids.length })}
      </InlineButton>
      {open ? (
        <span class="basis-full" data-based-on="">
          <RichText space={space} text={ids.map((id) => `#[${pick(id)?.title ?? ""}](${id})`).join(" ")} />
        </span>
      ) : null}
    </>
  );
}

/** Over a report: its label, the period it covers, what it's based on. */
export function ReportHead({ space, c }: { space: SpaceOut; c: CommentOut }) {
  const based = c.based_on ?? [];
  return (
    <p class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-600" data-report-head="">
      <Badge tone="blue">{c.label ?? t("attached.report")}</Badge>
      {c.period ? <span class="tabular-nums">{periodText(c.period)}</span> : null}
      {based.length > 0 ? <BasedOn space={space} ids={based} /> : null}
    </p>
  );
}

/**
 * A series of reports: its newest drawn by `draw`, then "N earlier reports", opening them under it, newest first, each
 * drawn in place. Opened already when the address points at one of them.
 */
export function SeriesView<T>({
  series,
  rootOf,
  draw,
}: {
  series: Series<T>;
  rootOf: (x: T) => CommentOut;
  draw: (x: T) => ComponentChildren;
}) {
  const hash = location.value.hash;
  const [open, setOpen] = useState(() => series.earlier.some((x) => hash === `#${commentAnchor(rootOf(x).id)}`));
  const n = series.earlier.length;
  return (
    <>
      {draw(series.latest)}
      {n > 0 ? (
        <li class="-mt-1 flex flex-col gap-2 pl-4" data-series={rootOf(series.latest).label ?? ""}>
          <span>
            <InlineButton size="xs" aria-expanded={open} onClick={() => setOpen(!open)}>
              {open ? t("reports.hideEarlier") : t("reports.earlier", { count: n })}
            </InlineButton>
          </span>
          {open ? <ul class="flex flex-col gap-2">{series.earlier.map((x) => draw(x))}</ul> : null}
        </li>
      ) : null}
    </>
  );
}
